// =============================================================================
// DECISION CARD - Картка варіанту рішення (Tactical HUD)
// =============================================================================

import { motion } from 'framer-motion';
import type { Option, Impact } from '../types';
import { PARAMETER_LABELS } from '@zavoruha.tech/holovkom-types';

interface DecisionCardProps {
  option: Option;
  onSelect: (option: Option) => void;
  disabled?: boolean;
  index: number;
}

export function DecisionCard({
  option,
  onSelect,
  disabled,
  index,
}: DecisionCardProps) {
  // Build impact chips array with stable order
  const impactChips = Object.entries(PARAMETER_LABELS).map(
    ([key, { emoji }]) => {
      const value = option.impact[key as keyof Impact];
      return { key, emoji, value };
    }
  );

  return (
    <motion.button
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.1, duration: 0.4 }}
      whileHover={
        disabled ? undefined : { backgroundColor: 'rgba(16, 185, 129, 0.08)' }
      }
      whileTap={disabled ? undefined : { scale: 0.98 }}
      onClick={() => !disabled && onSelect(option)}
      disabled={disabled}
      className={`
        w-full text-left p-5 md:p-6 transition-all duration-200
        group relative border border-war-dim/20 bg-war-panel/40
        ${disabled
          ? 'opacity-40 cursor-not-allowed border-war-dim/10'
          : 'cursor-pointer md:hover:border-war-primary/40 shadow-sm'
        }
      `}
    >
      {/* Corner accents - only animate on hover on desktop */}
      <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-war-primary/40 md:group-hover:border-war-primary transition-colors" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-war-primary/40 md:group-hover:border-war-primary transition-colors" />

      <div className="flex items-start gap-4">
        {/* Option Text */}
        <div className="flex-1 space-y-2">
          <h3
            className={`font-mono text-sm md:text-base leading-relaxed ${disabled
                ? 'text-war-dim'
                : 'text-war-bright md:group-hover:text-war-bright'
              }`}
          >
            {option.text}
          </h3>

          {/* Description - visible on mobile, hidden on desktop unless hover */}
          {option.description && !disabled && (
            <p className="text-war-dim text-xs font-mono leading-relaxed md:hidden">
              {option.description}
            </p>
          )}

          {/* Impact chips - always visible */}
          {!disabled && (
            <div className="flex flex-wrap gap-1.5 pt-2" aria-hidden="true">
              {impactChips.map(({ key, emoji, value }) => {
                const isPositive = value > 0;
                const isNegative = value < 0;
                const isNeutral = value === 0;

                return (
                  <span
                    key={key}
                    className={`
                      inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono font-bold border
                      ${isPositive
                        ? 'bg-war-primary/10 border-war-primary/30 text-war-primary'
                        : ''
                      }
                      ${isNegative
                        ? 'bg-war-red/10 border-war-red/30 text-war-red'
                        : ''
                      }
                      ${isNeutral
                        ? 'bg-war-dim/5 border-war-dim/10 text-war-dim opacity-40'
                        : ''
                      }
                    `}
                  >
                    <span>{emoji}</span>
                    <span>
                      {value > 0 ? '+' : ''}
                      {value}
                    </span>
                  </span>
                );
              })}
            </div>
          )}

          {/* Action indicator - visible on mobile, hover-only on desktop */}
          {!disabled && (
            <div className="flex items-center gap-2 pt-2 transition-all md:opacity-0 md:group-hover:opacity-100">
              <span className="text-war-primary text-[10px] font-mono animate-pulse uppercase tracking-[0.2em]">
                УХВАЛИТИ РІШЕННЯ &gt;&gt;
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Hover background effect - desktop only */}
      <div className="absolute inset-0 bg-gradient-to-r from-war-primary/5 to-transparent opacity-0 md:group-hover:opacity-100 transition-opacity pointer-events-none" />
    </motion.button>
  );
}

interface DecisionListProps {
  options: Option[];
  onSelect: (option: Option) => void;
  disabled?: boolean;
}

export function DecisionList({
  options,
  onSelect,
  disabled,
}: DecisionListProps) {
  return (
    <section className="space-y-4" aria-labelledby="decisions-heading">
      <div className="flex items-center gap-4 mb-6">
        <div className="h-px flex-1 bg-war-dim/20" />
        <h2
          id="decisions-heading"
          className="text-war-primary font-mono font-bold text-xs md:text-sm tracking-[0.3em] uppercase whitespace-nowrap"
        >
          ДОСТУПНІ ВАРІАНТИ ДІЙ
        </h2>
        <div className="h-px flex-1 bg-war-dim/20" />
      </div>

      <ul
        role="list"
        className="space-y-3"
        aria-label="Список варіантів рішень"
      >
        {options.map((option, index) => (
          <li key={option.id}>
            <DecisionCard
              option={option}
              onSelect={onSelect}
              disabled={disabled}
              index={index}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}
